import { useDispatch } from 'react-redux';

import { useComplianceLevelState, useDeviceState } from '@/hooks/store';
import { setComplianceLevel } from '@/store/complianceLevel/actions';
import { IS_GDPR_LIMITED, IS_LIMITED, REQUIRE_GDPR_MODAL } from '@/store/complianceLevel/types';
import { setUser } from '@/store/user/actions';
import { User } from '@/store/user/types';
import { initSegmentUser } from '@/utils/analytics/segment';
import { calmLogger } from '@/utils/calmLogger';
import { postMe } from '@/utils/endpoints';
import { CalmAuthMethods, setUserCookies } from '@/utils/privacyCookies';
import { setNextLocaleCookie } from '@/utils/setNextLocaleCookie';

import { setAmplitudeUserProperties } from '../../utils/analytics/amplitude';
import useIsCalmSleepApp from '../useIsCalmSleepApp';

export const useSetupUserAfterAuth = () => {
	const dispatch = useDispatch();
	const complianceLevel = useComplianceLevelState();
	const device = useDeviceState();

	const isCalmSleepApp = useIsCalmSleepApp();

	const setupUserAfterAuth = async (user: User, authMethod?: CalmAuthMethods) => {
		setUserCookies(user, authMethod);
		setNextLocaleCookie(device?.language);
		if (isCalmSleepApp && user.has_opted_in_limited_data_use) {
			user.compliance_level = complianceLevel === REQUIRE_GDPR_MODAL ? IS_GDPR_LIMITED : IS_LIMITED;
			dispatch(setComplianceLevel(user.compliance_level));
		}
		dispatch(setUser(user));

		const has_opted_in_limited_data_use =
			complianceLevel === IS_GDPR_LIMITED || complianceLevel === IS_LIMITED;

		if (isCalmSleepApp) {
			if (!user.has_opted_in_limited_data_use && has_opted_in_limited_data_use) {
				setAmplitudeUserProperties({
					ldu_opt_in_at: new Date().toISOString(),
				});
			}
		} else {
			await postMe({
				compliance_level: complianceLevel,
				has_opted_in_limited_data_use,
			}).catch(error =>
				calmLogger.error('Error in useSetupUserAfterAuth postMe', { has_opted_in_limited_data_use }, error),
			);
		}

		initSegmentUser(user.id).catch(error =>
			calmLogger.error('Error in useSetupUserAfterAuth initSegmentUser', {}, error),
		);
	};

	return setupUserAfterAuth;
};
